import { Capacitor } from '@capacitor/core';
import { SocialLogin } from '@capgo/capacitor-social-login';
import { 
  signInWithCredential, 
  signInWithCustomToken,
  GoogleAuthProvider, 
  OAuthProvider,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from './firebase';

const timeoutPromise = (promise, timeout) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Operation timed out')), timeout)
    )
  ]);
};

const IOS_CLIENT_ID = '1034477093602-6ej1ckdqurjlm01mfkh322rt58ial6fe.apps.googleusercontent.com';

class MobileAuth {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.initialized = false;
  }

  async initialize() {
    if (this.initialized) return;
    
    if (!this.isNative) {
      console.log('⚠️ Mobile auth not available on web platform');
      return;
    }
    
    try {
      console.log('🚀 Starting SvgTrace mobile auth initialization...');
      console.log('📱 Platform:', Capacitor.getPlatform());

      await setPersistence(auth, browserLocalPersistence);

      const isAndroid = Capacitor.getPlatform() === 'android';
      const initConfig = {};

      if (isAndroid) {
        initConfig.google = {
          clientId: process.env.REACT_APP_FIREBASE_API_KEY,
          useNativeUI: true
        };
      } else {
        initConfig.google = {
          iOSClientId: IOS_CLIENT_ID,
          iOSServerClientId: IOS_CLIENT_ID,
          useNativeUI: true
        };
      }

      if (!isAndroid) {
        initConfig.apple = {
          clientId: 'com.svgtrace.pro.web',
          useNativeUI: true
        };
      }

      await SocialLogin.initialize(initConfig);
      
      this.initialized = true;
      console.log('✅ Mobile auth initialized');
    } catch (error) {
      console.error('❌ Failed to initialize mobile auth:', error);
      throw error;
    }
  }

  async _attemptGoogleLogin() {
    await setPersistence(auth, browserLocalPersistence);

    const isAndroid = Capacitor.getPlatform() === 'android';
    const loginOptions = {
      scopes: ['email', 'profile'],
      useNativeUI: true
    };

    if (!isAndroid) {
      loginOptions.serverClientId = IOS_CLIENT_ID;
    }

    const result = await timeoutPromise(
      SocialLogin.login({
        provider: 'google',
        options: loginOptions,
      }),
      30000
    );

    if (!result?.result?.idToken) {
      throw new Error('No ID token received from Google login');
    }

    console.log('🎫 Creating Firebase credential from native token...');
    const credential = GoogleAuthProvider.credential(
      result.result.idToken,
      result.result.accessToken
    );

    const firebaseResult = await timeoutPromise(
      signInWithCredential(auth, credential),
      30000
    );

    console.log('✅ Google sign-in successful:', firebaseResult.user.email);
    return firebaseResult;
  }

  async signInWithGoogle() {
    if (!this.isNative) {
      throw new Error('Native social login is only available on mobile platforms.');
    }
    
    try {
      if (!this.initialized) {
        await this.initialize();
      }

      console.log('🔍 Attempting native Google login...');
      return await this._attemptGoogleLogin();
    } catch (firstError) {
      // On iOS the first attempt often fails right after SocialLogin.initialize()
      // because the underlying GIDSignIn SDK isn't fully ready yet. Retry once.
      console.warn('⚠️ First Google login attempt failed, retrying...', firstError.message);
      try {
        await new Promise(r => setTimeout(r, 600));
        return await this._attemptGoogleLogin();
      } catch (retryError) {
        console.error('❌ Google login failed on retry:', retryError.message);
        throw retryError;
      }
    }
  }

  async _generateNonce() {
    const array = new Uint8Array(32);
    window.crypto.getRandomValues(array);
    return Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
  }

  async _sha256(str) {
    const buffer = new TextEncoder().encode(str);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', buffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  async signInWithApple() {
    if (!this.isNative) {
      throw new Error('Native social login is only available on mobile platforms.');
    }
    
    try {
      if (!this.initialized) {
        await this.initialize();
      }
      
      console.log('🍎 Attempting native Apple login...');

      // Generate nonce — required by Firebase to prevent replay attacks
      const rawNonce = await this._generateNonce();
      const hashedNonce = await this._sha256(rawNonce);

      const result = await SocialLogin.login({
        provider: 'apple',
        options: {
          scopes: ['email', 'name'],
          nonce: hashedNonce,
        },
      });

      console.log('🍎 Apple login raw result:', JSON.stringify(result?.result ? {
        hasIdToken: !!result.result.idToken,
        idTokenLength: result.result.idToken?.length,
        profile: result.result.profile,
      } : result));

      if (!result?.result?.idToken) {
        throw new Error('No identity token received from Apple login');
      }

      const appleIdToken = result.result.idToken;

      // Native iOS tokens have aud = bundle ID, but Firebase web SDK expects aud = Service ID.
      // We use a Cloud Function to verify the token server-side and return a custom token instead.
      console.log('🍎 Exchanging Apple token via Cloud Function...');
      const functions = getFunctions();
      const appleSignIn = httpsCallable(functions, 'appleSignIn');
      const { data } = await timeoutPromise(appleSignIn({ idToken: appleIdToken }), 30000);

      if (!data?.customToken) {
        throw new Error('No custom token returned from appleSignIn function');
      }

      const firebaseResult = await timeoutPromise(
        signInWithCustomToken(auth, data.customToken),
        15000
      );
      
      console.log('✅ Apple sign-in successful:', firebaseResult.user.email);
      return firebaseResult;
    } catch (error) {
      console.error('❌ Apple login failed:', error.message, 'code:', error.code, 'full:', error);
      throw error;
    }
  }

  async logout(provider = 'google') {
    if (!this.isNative) return;
    try {
      await SocialLogin.logout({ provider });
    } catch (error) {
      console.error(`❌ Failed to logout from ${provider}:`, error.message);
    }
  }

  isNativePlatform() {
    return this.isNative;
  }
}

export const mobileAuth = new MobileAuth();
